import { type AnimationDefinition, motion, useReducedMotionConfig } from 'motion/react';
import { memo, type ReactNode, useCallback, useEffect, useRef } from 'react';

import { ONE_SECOND } from '@air/utilities';

import {
  getWorkspaceAgentSpaceShellVariants,
  WORKSPACE_AGENT_SPACE_BAR_MIN_HEIGHT_PX,
} from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentSpaceMotion';

export type WorkspaceAgentSpaceShellProps = {
  /** When false (ui2026) the shell slides off by the smaller `bottom-2` clearance instead of the legacy 40px strip. */
  reservesStatusBarStrip: boolean;
  /** Shell is still open over a selection that has already gone. Keeps glass on for the slide-out. */
  isCollapsing: boolean;
  /** Drives the slide target — stays mounted while `isBarDrawn` so bar content keeps live collapse props. */
  isShown: boolean;
  /** Advances once the hide slide lands. Enter complete is ignored. */
  onShellSettled: () => void;
  /** Selection chrome — rides with the shell; no separate enter/exit animation. */
  barContent: ReactNode;
};

/**
 * Frosted selection bar shell. Slides up from below the stage when a selection appears and slides
 * back down when it clears — no FAB morph.
 */
export const WorkspaceAgentSpaceShell = memo(
  ({ reservesStatusBarStrip, isCollapsing, isShown, onShellSettled, barContent }: WorkspaceAgentSpaceShellProps) => {
    const shouldReduceMotion = useReducedMotionConfig();
    const variants = getWorkspaceAgentSpaceShellVariants(!!shouldReduceMotion, reservesStatusBarStrip);
    const hideSettleTimeoutRef = useRef<number | undefined>(undefined);

    const handleAnimationComplete = useCallback(
      (definition: AnimationDefinition) => {
        // Enter complete must not settle — a mid-enter clear still needs the shell mounted
        // for the slide-out. Hide landing is what advances the bar freeze.
        if (definition !== 'hidden') {
          return;
        }

        if (hideSettleTimeoutRef.current !== undefined) {
          window.clearTimeout(hideSettleTimeoutRef.current);
          hideSettleTimeoutRef.current = undefined;
        }

        onShellSettled();
      },
      [onShellSettled],
    );

    useEffect(() => {
      if (isShown) {
        return undefined;
      }

      // Motion skips `onAnimationComplete` when hide is already at the hidden y (clear before the
      // first enter frame). The timer is the fallback; hide complete cancels it.
      hideSettleTimeoutRef.current = window.setTimeout(
        onShellSettled,
        variants.hidden.transition.duration * ONE_SECOND,
      );

      return () => {
        if (hideSettleTimeoutRef.current !== undefined) {
          window.clearTimeout(hideSettleTimeoutRef.current);
          hideSettleTimeoutRef.current = undefined;
        }
      };
    }, [isShown, onShellSettled, variants.hidden.transition.duration]);

    return (
      <motion.div
        animate={isShown ? 'shown' : 'hidden'}
        className="flex w-full items-stretch overflow-visible rounded-16 bg-black/55 shadow-m backdrop-blur-[24px]"
        data-disableselect="true"
        data-testid="WORKSPACE_AGENT_SPACE"
        inert={isCollapsing ? true : undefined}
        initial="hidden"
        onAnimationComplete={handleAnimationComplete}
        style={{
          minHeight: WORKSPACE_AGENT_SPACE_BAR_MIN_HEIGHT_PX,
          // Parent portal is `pointer-events-none` so empty stage area stays clickable.
          pointerEvents: isCollapsing ? 'none' : 'auto',
        }}
        variants={variants}
      >
        <div className="flex min-w-0 flex-1 items-center overflow-hidden">{barContent}</div>
      </motion.div>
    );
  },
);

WorkspaceAgentSpaceShell.displayName = 'WorkspaceAgentSpaceShell';
